import {
  Dialog,
  Typography,
  Box,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  Chip,
  FormControlLabel,
  FormGroup,
  Checkbox,
} from "@mui/material";
import {
  CustomerUserDto,
  ValidateEmailRequest,
} from "@/app/models/accountProfile/types";
import {
  OptionGroup,
  SelectListItemDto,
  AddressDropdownItem,
} from "@/app/models/checkout/types";
import { useEffect, useRef, useState } from "react";
import dataService from "@/app/services/dataService";
import {
  OrderNotificationResponse,
  ShipToSettingModel,
} from "@/app/models/orderNotification/types";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  OrderNotificationPreferences,
  OrderNotificationPreferencesHandle,
} from "../checkout/orderPlaced/orderNotificationPreferences";

interface SecondaryUserDialogProps {
  mode: "add" | "edit";
  open: boolean;
  isOrderNotificationSelected: boolean;
  isSaveDisabled: boolean;
  user: CustomerUserDto | null;
  validationErrors: Record<string, string>;
  availableRoles: SelectListItemDto[];
  customerShipTos: OptionGroup[];
  customerShipTosLoading: boolean;
  onSave: (shipTos: ShipToSettingModel[]) => void;
  onClose: () => void;
  toggleRole: (roleValue: string, checked: boolean) => void;
  updateUserField: (field: keyof CustomerUserDto, value: string) => void;
}

export function SecondaryUserDialog({
  mode,
  open,
  isOrderNotificationSelected,
  isSaveDisabled,
  user,
  validationErrors,
  availableRoles,
  customerShipTos,
  customerShipTosLoading,
  onSave,
  onClose,
  toggleRole,
  updateUserField,
}: SecondaryUserDialogProps) {
  const [shipToloading, setShipToLoading] = useState(false);
  const [orderNotificationLoading, setOrderNotificationLoading] =
    useState(false);
  const [userShipTos, setUserShipTos] = useState<AddressDropdownItem[]>([]);
  const [orderNotificationModel, setOrderNotificationModel] =
    useState<OrderNotificationResponse>();
  const orderNotificationRef =
    useRef<OrderNotificationPreferencesHandle | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isEdit = mode === "edit";

  const [emailServerError, setEmailServerError] = useState<string | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  useEffect(() => {
    if (open && user?.id) {
      fetchUserShipTos();
    }
  }, [open, user?.id]);

  useEffect(() => {
    if (open && isOrderNotificationSelected && user?.id) {
      loadOrderNotifications();
    }
  }, [open, isOrderNotificationSelected, user?.id]);

  useEffect(() => {
    if (!open) {
      setEmailServerError(null);
      setIsCheckingEmail(false);
      return;
    }

    const email = user?.email?.trim();
    if (!email) {
      setEmailServerError(null);
      return;
    }

    const payload: ValidateEmailRequest = {
      email: email,
      customerId: user?.id ?? 0,
    };

    setIsCheckingEmail(true);
    const handle = setTimeout(async () => {
      try {
        const res = await dataService.validateSecondaryUserEmail(payload);

        if (res?.success && res.data?.success) {
          setEmailServerError(null);
        } else {
          setEmailServerError(
            res?.data?.message || "This email is already in use."
          );
        }
      } catch (err: any) {
        setEmailServerError(
          err?.response?.data?.message || "Unable to validate email."
        );
      } finally {
        setIsCheckingEmail(false);
      }
    }, 500);

    return () => {
      clearTimeout(handle);
    };
  }, [open, user?.email, user?.id]);

  const loadOrderNotifications = async () => {
    try {
      if (!user?.id) return;
      setOrderNotificationLoading(true);
      const res = await dataService.getOrderNotification(user?.id);
      if (res?.success && res.data && !res.data.isAccessDenied) {
        setOrderNotificationModel(res.data as OrderNotificationResponse);
      }
    } finally {
      setOrderNotificationLoading(false);
    }
  };

  const fetchUserShipTos = async () => {
    try {
      if (!user?.id) return;
      setShipToLoading(true);
      const response = await dataService.getUserSelectedShipTos(user?.id);
      if (response && response.data && response.data.shipTos?.length > 0) {
        setUserShipTos(
          response.data.shipTos.map((x) => ({
            id: x.shipTo,
            name: x.shipToName,
          })) ?? []
        );
      }
    } catch (error) {
      console.error("Failed to load user shiptos:", error);
    } finally {
      setShipToLoading(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      className="dialogScroll"
      PaperProps={{ className: "customDialogPaper" }}
    >
      {/* TITLE */}
      <Typography className="dialogTitle" sx={{ mt: 2 }}>
        {isEdit ? "– Edit Secondary User –" : "– Secondary User Creation –"}
      </Typography>

      {/* SUBTITLE */}
      <Typography
        sx={{
          textAlign: "center",
          color: "#494440",
          fontSize: "11.6px",
          fontFamily: '"Montserrat", Verdana, sans-serif;',
          mb: 4,
        }}
      >
        {isEdit ? (
          <>
            You can edit a secondary user for your account.
            <br /> Please modify the information below and press “Save”.
          </>
        ) : (
          <>
            This is where you create a secondary user for your account.
            <br /> After you press “Save”, the user will receive a welcome email
            to LansingNOW.
          </>
        )}
      </Typography>

      <Box sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* FIRST NAME */}
        <Typography sx={{ mb: 1, fontWeight: 600 }}>
          First Name <span style={{ color: "#62b5e5" }}>*</span>
        </Typography>

        <TextField
          className="dialogTextField"
          variant="standard"
          value={user?.firstName ?? ""}
          onChange={(e) => updateUserField("firstName", e.target.value)}
          error={Boolean(validationErrors.firstName)}
          helperText={validationErrors.firstName || " "}
          fullWidth
        />

        {/* LAST NAME */}
        <Typography sx={{ mb: 1, fontWeight: 600 }}>
          Last Name <span style={{ color: "#62b5e5" }}>*</span>
        </Typography>

        <TextField
          className="dialogTextField"
          variant="standard"
          value={user?.lastName ?? ""}
          onChange={(e) => updateUserField("lastName", e.target.value)}
          error={Boolean(validationErrors.lastName)}
          helperText={validationErrors.lastName || " "}
          fullWidth
        />

        <Typography sx={{ mb: 1, fontWeight: 600 }}>
          Email <span style={{ color: "#62b5e5" }}>*</span>
        </Typography>

        <TextField
          className="dialogTextField"
          variant="standard"
          type="email"
          value={user?.email ?? ""}
          onChange={(e) => updateUserField("email", e.target.value)}
          error={Boolean(validationErrors.email)}
          helperText={validationErrors.email || emailServerError || " "}
          fullWidth
        />

        {customerShipTosLoading ? (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          customerShipTos.length > 0 && (
            <>
              <Typography className="dialogTitle">
                - Limit Assigned Ship To -
              </Typography>
              <Box className="dropdown-filter">
                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  options={customerShipTos.flatMap((group) => group.options)}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  value={userShipTos}
                  onChange={(_, newValue) => {
                    setUserShipTos(newValue as AddressDropdownItem[]);
                  }}
                  filterSelectedOptions
                  slotProps={{ popper: { sx: { zIndex: 2100 } } }}
                  renderTags={(value, getTagProps) =>
                    value.map((o, i) => (
                      <Chip
                        label={typeof o === "string" ? o : o.name}
                        variant="outlined"
                        {...getTagProps({ index: i })}
                        key={typeof o === "string" ? o : o.id}
                        sx={{ fontSize: { xs: 11, md: 14 } }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        userShipTos.length === 0
                          ? "All ship-tos currently selected"
                          : ""
                      }
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {shipToloading && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{ fontSize: { xs: 11, md: 14 } }}
                    />
                  )}
                  ListboxProps={{
                    sx: {
                      fontSize: { xs: 11, md: 16 },
                    },
                  }}
                />
              </Box>{" "}
            </>
          )
        )}

        {/* ROLES TITLE */}
        <Typography
          className="dialogTitle"
          sx={{
            mt: 6,
          }}
        >
          – User Roles –
        </Typography>

        <Typography
          sx={{
            fontSize: "11px",
            textAlign: "center",
            color: "#494440",
            mb: 3,
          }}
        >
          Please select which user roles you would like the secondary user to
          have. You can always come back and edit these later.
        </Typography>

        {/* ROLES CHECKBOXES */}
        <Stack
          direction="column"
          spacing={0.5}
          sx={{ mb: 1 }}
          alignItems="center"
        >
          <FormGroup>
            {availableRoles.map((role) => (
              <FormControlLabel
                key={role.value}
                control={
                  <Checkbox
                    checked={user?.availableCustomerRoles?.some(
                      (r) => r.value === role.value && r.selected
                    )}
                    onChange={(e) => toggleRole(role.value, e.target.checked)}
                    size="small"
                    sx={{
                      padding: "2px",
                      "& .MuiSvgIcon-root": {
                        fontSize: "20px",
                      },
                    }}
                  />
                }
                label={role.text}
                sx={{
                  fontSize: "14px",
                  textAlign: "left",
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    fontFamily: '"Montserrat", Verdana, sans-serif',
                    color: "#494440",
                  },
                }}
              />
            ))}
          </FormGroup>
        </Stack>

        {/* ORDER NOTIFICATION SECTION (EDIT MODE ONLY) */}
        {isEdit && isOrderNotificationSelected && (
          <>
            {orderNotificationLoading ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontSize: "13px", color: "#494440" }}>
                  {orderNotificationModel && (
                    <OrderNotificationPreferences
                      ref={orderNotificationRef}
                      model={orderNotificationModel}
                      isAccount={true}
                      showHeadings={false}
                      className="orderNotificationInDialog"
                      manageUserId={user?.id}
                    />
                  )}
                </Typography>
              </Box>
            )}
          </>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mt: 4,
            mb: 2,
          }}
        >
          <Button
            className="grayButtonOutline"
            sx={{ minWidth: 140 }}
            onClick={onClose}
          >
            CANCEL
          </Button>
          <Button
            className="filledBlueButton blueButtonOutline"
            sx={{ minWidth: 140 }}
            disabled={isSaveDisabled}
            onClick={async () => {
              if (isCheckingEmail || emailServerError) return;
              if (
                isEdit &&
                isOrderNotificationSelected &&
                orderNotificationRef.current
              ) {
                await orderNotificationRef.current.save();
              }

              onSave(
                userShipTos.map((x) => ({
                  shipTo: x.id,
                  shipToName: x.name,
                })) ?? []
              );
            }}
          >
            SAVE
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
